import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import AuthModal from "../components/AuthModal";
import useExperiment from "../hooks/useExperiment";
import { toast } from "sonner";
import { 
  Zap, 
  Target, 
  Brain, 
  Users, 
  Sparkles,
  ArrowRight,
  Check,
  ChevronRight,
  Upload,
  FileText,
  MessageSquare,
  TrendingUp,
  Shield,
  Minus,
  ShoppingBag
} from "lucide-react";

const RECRUITER_TIERS = [
  {
    id: "recruiter_starter",
    name: "Starter",
    price: 29,
    features: [
      "3 active job postings",
      "AI job descriptions (10/mo)",
      "Basic candidate ranking",
      "Compare 2 candidates side-by-side",
      "5 AI interviews/month",
      "Email & push notifications",
      "CRM pipeline access"
    ],
    highlighted: false
  },
  {
    id: "recruiter_pro",
    name: "Pro",
    price: 79,
    features: [
      "10 active job postings",
      "Unlimited AI job descriptions",
      "20 AI interviews/month",
      "Compare 5 candidates side-by-side",
      "Save job templates",
      "Bulk candidate messaging",
      "Applicant pipeline dashboard",
      "Interview scheduling"
    ],
    highlighted: true
  },
  {
    id: "recruiter_elite",
    name: "Elite",
    price: 149,
    features: [
      "Unlimited job postings",
      "Unlimited AI interviews",
      "Unlimited candidate comparisons",
      "Priority matching algorithm",
      "Export reports (PDF & JSON)",
      "Advanced analytics dashboard",
      "Save templates & outreach tools",
      "Video interview calls"
    ],
    highlighted: false
  }
];

const JOBSEEKER_TIERS = [
  {
    id: "jobseeker_free",
    name: "Free",
    price: 0,
    features: [
      "Profile creation & resume upload",
      "Apply to jobs",
      "Basic role-fit score",
      "2 AI practice interviews/mo",
      "Community & events access",
      "Badge & achievement system"
    ],
    highlighted: false
  },
  {
    id: "jobseeker_plus",
    name: "Plus",
    price: 9,
    features: [
      "Enhanced profile visibility",
      "AI career insights",
      "10 AI practice interviews/mo",
      "Personalized job recommendations",
      "Email digests & interview reminders",
      "Market insights access",
      "For You recommendations feed"
    ],
    highlighted: true
  },
  {
    id: "jobseeker_pro",
    name: "Pro",
    price: 19,
    features: [
      "Priority ranking in searches",
      "Unlimited AI interviews",
      "AI interview feedback & scoring",
      "Resume optimization for jobs",
      "Skill gap analysis",
      "Early access to new jobs",
      "Career roadmap PDF export",
      "Advanced analytics & reports"
    ],
    highlighted: false
  }
];

const RECRUITER_PAYG_ITEMS = [
  { name: "Promote a Job", desc: "Boost job visibility with priority placement in search results", prices: "$29/7d · $49/14d · $79/30d" },
  { name: "Unlock Candidate Contacts", desc: "Reveal email and phone number for candidates you want to reach", prices: "5 for $9 · 15 for $19" },
  { name: "Outreach Credits", desc: "Directly message candidates who haven't applied to your jobs", prices: "10 for $19 · 25 for $39" },
  { name: "AI Credits", desc: "Power AI job descriptions, interview screening, and candidate analysis", prices: "10 for $5 · 25 for $10 · 50 for $15" },
];

const JOBSEEKER_PAYG_ITEMS = [
  { name: "AI CV Generation", desc: "Generate a tailored, professional CV optimized for your target role", prices: "Via AI credits" },
  { name: "AI Interview Practice", desc: "Simulate real interviews with AI feedback on your answers", prices: "Via AI credits" },
  { name: "AI Cover Letter", desc: "Create a personalized cover letter matched to the job posting", prices: "Via AI credits" },
  { name: "Salary Negotiation AI", desc: "Get data-driven negotiation strategies based on your experience", prices: "Via AI credits" },
  { name: "AI Credits", desc: "Buy credits to use across all AI-powered tools on the platform", prices: "10 for $5 · 25 for $10 · 50 for $15" },
];

const RECRUITER_COMPARISON = {
  tiers: ["Starter", "Pro", "Elite"],
  features: [
    { name: "Active Job Postings", values: ["3", "10", "Unlimited"] },
    { name: "AI Job Descriptions", values: ["10/mo", "Unlimited", "Unlimited"] },
    { name: "AI Interviews", values: ["5/mo", "20/mo", "Unlimited"] },
    { name: "Candidate Comparisons", values: ["2", "5", "Unlimited"] },
    { name: "Candidate Ranking", values: ["Basic", "Advanced", "Priority"] },
    { name: "Save Job Templates", values: [false, true, true] },
    { name: "Bulk Messaging", values: [false, true, true] },
    { name: "Applicant Pipeline", values: [true, true, true] },
    { name: "CRM Pipeline", values: [true, true, true] },
    { name: "Export Reports", values: [false, false, true] },
    { name: "Priority Matching", values: [false, false, true] },
    { name: "Advanced Analytics", values: [false, false, true] },
    { name: "Video Interview Calls", values: [false, false, true] },
  ]
};

const JOBSEEKER_COMPARISON = {
  tiers: ["Free", "Plus", "Pro"],
  features: [
    { name: "Profile & Resume Upload", values: [true, true, true] },
    { name: "Apply to Jobs", values: [true, true, true] },
    { name: "Role-Fit Score", values: ["Basic", "Detailed", "Detailed"] },
    { name: "AI Practice Interviews", values: ["2/mo", "10/mo", "Unlimited"] },
    { name: "Profile Visibility", values: ["Standard", "Enhanced", "Priority"] },
    { name: "Career Insights", values: [false, true, true] },
    { name: "Job Recommendations", values: [false, true, true] },
    { name: "Email Digests", values: [false, true, true] },
    { name: "Market Insights", values: [false, true, true] },
    { name: "AI Interview Feedback", values: [false, false, true] },
    { name: "Resume Optimization", values: [false, false, true] },
    { name: "Skill Gap Analysis", values: [false, false, true] },
    { name: "Early Job Access", values: [false, false, true] },
    { name: "Career Roadmap PDF", values: [false, false, true] },
  ]
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, setUser } = useAuth();
  const [pricingTab, setPricingTab] = useState("recruiter");
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currencyInfo, setCurrencyInfo] = useState({ currency: "USD", symbol: "$" });
  const [pricing, setPricing] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [showCurrencySelector, setShowCurrencySelector] = useState(false);
  const [comparisonHighlight, setComparisonHighlight] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const scrollToPricing = (e) => {
    e.preventDefault();
    const el = document.getElementById("pricing");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // After scroll completes, flash the comparison table
      setTimeout(() => {
        setComparisonHighlight(true);
        setTimeout(() => setComparisonHighlight(false), 1500);
      }, 800);
    }
  };
  const [referralCode, setReferralCode] = useState(null);

  // A/B Testing: Landing CTA button experiment
  const { 
    variant: ctaVariant, 
    config: ctaConfig, 
    trackConversion: trackCtaConversion,
    isInExperiment: isInCtaExperiment 
  } = useExperiment("landing_cta");

  // A/B Testing: Signup social proof experiment  
  const { 
    variant: signupVariant, 
    config: signupConfig,
    trackConversion: trackSignupConversion,
    isInExperiment: isInSignupExperiment 
  } = useExperiment("signup_social_proof");

  // Capture referral code from URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref);
      // Store in localStorage for registration
      localStorage.setItem("referral_code", ref);
      toast.success("Referral code applied! Sign up to receive your reward.");
      // Automatically show auth modal for referral visitors
      if (!user) {
        setShowAuthModal(true);
      }
    }
  }, [searchParams, user]);

  // Helper function to map locale to currency
  const getCurrencyFromLocale = (locale) => {
    // Common locale to currency mappings
    const localeCurrencyMap = {
      'en-US': 'USD',
      'en-GB': 'GBP',
      'en-AU': 'AUD',
      'en-CA': 'CAD',
      'en-NG': 'NGN',
      'en-IN': 'INR',
      'en-ZA': 'ZAR',
      'de-DE': 'EUR',
      'de-AT': 'EUR',
      'fr-FR': 'EUR',
      'es-ES': 'EUR',
      'it-IT': 'EUR',
      'nl-NL': 'EUR',
      'pt-BR': 'BRL',
      'ja-JP': 'JPY',
      'zh-CN': 'CNY',
      'ko-KR': 'KRW',
      'ar-AE': 'AED',
      'ar-SA': 'SAR',
      'hi-IN': 'INR',
      'pl-PL': 'PLN',
      'ru-RU': 'RUB',
      'tr-TR': 'TRY',
      'th-TH': 'THB',
      'vi-VN': 'VND',
      'id-ID': 'IDR',
      'ms-MY': 'MYR',
      'fil-PH': 'PHP',
      'en-PH': 'PHP',
      'en-SG': 'SGD',
      'zh-SG': 'SGD',
    };
    
    // Direct match
    if (localeCurrencyMap[locale]) {
      return localeCurrencyMap[locale];
    }
    
    // Try matching just the region code (e.g., "en-NG" -> "NGN" for Nigeria)
    const regionCode = locale.split('-')[1]?.toUpperCase();
    if (regionCode) {
      // Special cases for countries with their own currency code matching region
      const regionCurrencyMap = {
        'US': 'USD', 'GB': 'GBP', 'AU': 'AUD', 'CA': 'CAD', 
        'NG': 'NGN', 'IN': 'INR', 'ZA': 'ZAR', 'BR': 'BRL',
        'JP': 'JPY', 'CN': 'CNY', 'KR': 'KRW', 'AE': 'AED',
        'SA': 'SAR', 'PL': 'PLN', 'RU': 'RUB', 'TR': 'TRY',
        'TH': 'THB', 'VN': 'VND', 'ID': 'IDR', 'MY': 'MYR',
        'PH': 'PHP', 'SG': 'SGD', 'CH': 'CHF', 'SE': 'SEK',
        'NO': 'NOK', 'DK': 'DKK', 'NZ': 'NZD', 'HK': 'HKD',
        // Euro zone countries
        'DE': 'EUR', 'FR': 'EUR', 'ES': 'EUR', 'IT': 'EUR',
        'NL': 'EUR', 'BE': 'EUR', 'AT': 'EUR', 'PT': 'EUR',
        'IE': 'EUR', 'FI': 'EUR', 'GR': 'EUR'
      };
      if (regionCurrencyMap[regionCode]) {
        return regionCurrencyMap[regionCode];
      }
    }
    
    return null; // No match found, will fall back to IP detection
  };

  // Detect user's currency on mount - using browser locale first, then IP fallback
  useEffect(() => {
    const detectCurrency = async () => {
      let detectedCurrency = null;
      let currencySource = 'default';
      
      // Step 1: Try browser locale detection first
      try {
        const browserLocale = navigator.language || navigator.userLanguage;
        if (browserLocale) {
          const localeCurrency = getCurrencyFromLocale(browserLocale);
          if (localeCurrency) {
            detectedCurrency = localeCurrency;
            currencySource = 'locale';
            console.log(`Currency detected from browser locale (${browserLocale}): ${localeCurrency}`);
          }
        }
      } catch (e) {
        console.log("Browser locale detection failed, falling back to IP");
      }
      
      // Step 2: Fall back to IP-based detection if locale didn't work
      if (!detectedCurrency) {
        try {
          const response = await fetch(`${API}/subscription/detect-currency`, {
            credentials: "include"
          });
          if (response.ok) {
            const data = await response.json();
            detectedCurrency = data.currency;
            currencySource = 'ip';
            
            setCurrencyInfo({
              currency: data.currency,
              symbol: data.symbol,
              name: data.name,
              country: data.country_name
            });
          }
        } catch (error) {
          console.error("IP currency detection failed:", error);
        }
      }
      
      // Step 3: If we got currency from locale, fetch the currency info
      if (detectedCurrency && currencySource === 'locale') {
        // We have a locale-based currency, fetch full info from API
        try {
          const response = await fetch(`${API}/subscription/pricing?currency=${detectedCurrency}`, {
            credentials: "include"
          });
          if (response.ok) {
            const data = await response.json();
            setCurrencyInfo({
              currency: data.currency,
              symbol: data.symbol,
              name: data.currency_name || detectedCurrency,
              country: null // From locale, not IP
            });
          }
        } catch (error) {
          // If that fails, just use the detected currency with defaults
          setCurrencyInfo({
            currency: detectedCurrency,
            symbol: detectedCurrency === 'USD' ? '$' : detectedCurrency,
            name: detectedCurrency,
            country: null
          });
        }
      }
      
      // Step 4: Fetch pricing with detected currency
      if (detectedCurrency) {
        fetchPricing(detectedCurrency, billingPeriod);
      }
      
      // Fetch available currencies for selector
      try {
        const currResponse = await fetch(`${API}/subscription/currencies`);
        if (currResponse.ok) {
          const currData = await currResponse.json();
          setAvailableCurrencies(currData.currencies || []);
        }
      } catch (error) {
        console.error("Failed to fetch currencies:", error);
      }
    };
    
    detectCurrency();
  }, []);

  // Refetch pricing when billing period changes
  useEffect(() => {
    if (currencyInfo.currency) {
      fetchPricing(currencyInfo.currency, billingPeriod);
    }
  }, [billingPeriod, currencyInfo.currency]);

  const fetchPricing = async (currency, billing = "monthly") => {
    try {
      const response = await fetch(`${API}/subscription/pricing?currency=${currency}&billing=${billing}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    }
  };

  const handleCurrencyChange = (newCurrency) => {
    const currInfo = availableCurrencies.find(c => c.code === newCurrency);
    if (currInfo) {
      setCurrencyInfo({
        currency: currInfo.code,
        symbol: currInfo.symbol,
        name: currInfo.name,
        country: null // Manual selection
      });
      fetchPricing(newCurrency, billingPeriod);
    }
    setShowCurrencySelector(false);
  };

  // Get price for a tier
  const getTierPrice = (tierId, defaultPrice) => {
    if (!pricing) return { formatted: `$${defaultPrice}`, symbol: "$", amount: defaultPrice, savings: null };
    
    const allTiers = [...(pricing.recruiter_tiers || []), ...(pricing.jobseeker_tiers || [])];
    const tier = allTiers.find(t => t.id === tierId);
    
    if (tier) {
      return {
        formatted: tier.price_formatted,
        symbol: pricing.symbol,
        amount: tier.price,
        annualTotal: tier.annual_total_formatted,
        savings: tier.savings_formatted,
        savingsAmount: tier.savings
      };
    }
    return { formatted: `$${defaultPrice}`, symbol: "$", amount: defaultPrice, savings: null };
  };

  const handleGetStarted = () => {
    // Track A/B test conversion when user clicks CTA
    if (isInCtaExperiment) {
      trackCtaConversion("signup_click", { button_variant: ctaVariant });
    }
    
    if (user) {
      if (!user.role) {
        navigate("/role-selection");
      } else if (user.role === "recruiter") {
        navigate("/dashboard/recruiter");
      } else {
        navigate("/dashboard/jobseeker");
      }
    } else {
      // Show auth modal instead of direct redirect
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = (userData) => {
    // Update the user state in context
    setUser(userData);
    
    // Determine the correct redirect path
    let redirectPath = "/dashboard/jobseeker";
    if (!userData.role) {
      redirectPath = "/role-selection";
    } else if (userData.role === "admin") {
      redirectPath = "/admin";
    } else if (userData.role === "recruiter") {
      redirectPath = "/dashboard/recruiter";
    }
    
    // Close modal state first
    setShowAuthModal(false);
    
    // Navigate with a slight delay to ensure state is updated
    // Using window.location for a guaranteed hard navigation as fallback
    try {
      navigate(redirectPath, { replace: true });
    } catch (e) {
      // Fallback to window.location if navigate fails
      window.location.href = redirectPath;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-crimson flex items-center justify-center">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="font-heading text-lg sm:text-2xl font-bold tracking-tight">REKRUTOR</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm uppercase tracking-widest text-gray-400 hover:text-crimson transition-colors">Features</a>
            <a href="#pricing" onClick={scrollToPricing} className="text-sm uppercase tracking-widest text-gray-400 hover:text-crimson transition-colors">Pricing</a>
            <Link to="/subscription" className="text-sm uppercase tracking-widest text-gray-400 hover:text-crimson transition-colors">Subscribe</Link>
            <Link to="/about" className="text-sm uppercase tracking-widest text-gray-400 hover:text-crimson transition-colors">About Us</Link>
            <button 
              onClick={() => {
                if (window.__pwaInstallPrompt) {
                  window.__pwaInstallPrompt.prompt();
                  window.__pwaInstallPrompt.userChoice.then(({ outcome }) => {
                    if (outcome === 'accepted') {
                      localStorage.setItem('pwa-just-installed', 'true');
                    }
                    window.__pwaInstallPrompt = null;
                  });
                } else if (window.matchMedia('(display-mode: standalone)').matches) {
                  toast.success("Rekrutor is already installed!");
                } else {
                  toast("To install: tap your browser's Share button, then 'Add to Home Screen'", { duration: 5000 });
                }
              }}
              className="text-sm uppercase tracking-widest text-gray-400 hover:text-crimson transition-colors"
              data-testid="get-app-btn"
            >Get App</button>
          </div>
          <Button 
            onClick={handleGetStarted}
            className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-3 sm:px-6 text-xs sm:text-sm"
            data-testid="nav-get-started-btn"
          >
            {loading ? "..." : user ? "Dashboard" : "Login / Sign Up"}
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-crimson/10 to-transparent pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[800px] h-[400px] sm:h-[800px] bg-crimson/5 rounded-full blur-3xl" />
        
        <motion.div 
          className="max-w-7xl mx-auto grid grid-cols-12 gap-4 sm:gap-8 items-center relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="col-span-12 md:col-span-7" variants={itemVariants}>
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-crimson/10 border border-crimson/30 mb-4 sm:mb-6">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-crimson" />
              <span className="text-xs sm:text-sm uppercase tracking-widest text-crimson">AI-Powered Recruitment</span>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-none mb-4 sm:mb-6">
              BREAK THE<br />
              <span className="text-crimson">MATRIX</span> OF<br />
              TRADITIONAL<br />
              HIRING
            </h1>
            
            <p className="text-sm sm:text-lg text-gray-400 max-w-xl mb-6 sm:mb-8 leading-relaxed">
              The red pill for career pivots. Connect mid-career professionals with forward-thinking recruiters through AI-powered matching, interviews, and insights.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                onClick={handleGetStarted}
                className={`bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-4 sm:px-8 py-4 sm:py-6 text-sm sm:text-lg glow-red ${
                  ctaConfig?.button_style === "pulse" ? "animate-pulse" : ""
                }`}
                data-testid="hero-get-started-btn"
              >
                {ctaConfig?.button_text || "Start Your Journey"} <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button 
                variant="outline"
                className="border-[#333] text-white hover:border-crimson hover:text-crimson uppercase tracking-wider px-4 sm:px-8 py-4 sm:py-6 text-sm"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-learn-more-btn"
              >
                Learn More
              </Button>
            </div>
          </motion.div>
          
          <motion.div 
            className="col-span-12 md:col-span-5 relative"
            variants={itemVariants}
          >
            <div className="relative aspect-square">
              <div className="absolute inset-0 bg-gradient-to-br from-crimson/20 to-transparent rounded-sm" />
              <img 
                src="/images/hero-team.jpeg"
                alt="Diverse professionals across industries — Rekrutor connects talent everywhere"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-crimson/30" />
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-crimson/20 -z-10" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-8 sm:py-12 border-y border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {[
            { value: "10K+", label: "Career Pivots" },
            { value: "500+", label: "Recruiters" },
            { value: "95%", label: "Match Rate" },
            { value: "24h", label: "Avg. Response" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-crimson">{stat.value}</div>
              <div className="text-xs sm:text-sm uppercase tracking-widest text-gray-500 mt-1 sm:mt-2">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              FEATURES THAT <span className="text-crimson">DISRUPT</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              Everything you need to revolutionize your hiring or job search journey
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: Brain,
                title: "AI Job Descriptions",
                description: "Generate ATS-optimized, bias-free job postings in seconds. No copywriting expertise needed."
              },
              {
                icon: MessageSquare,
                title: "AI Interviewer",
                description: "Conduct AI-powered screenings 24/7 with voice mode, video recording, and automated scoring."
              },
              {
                icon: Target,
                title: "Smart Matching",
                description: "Advanced algorithms rank candidates by skills, experience, and role-fit with percentage scores."
              },
              {
                icon: TrendingUp,
                title: "Career Insights & Analytics",
                description: "Personalized career roadmaps, skill gap analysis, market trends, and exportable PDF reports."
              },
              {
                icon: FileText,
                title: "Resume Optimization",
                description: "AI analyzes your resume against job postings — highlights strengths, gaps, and action items."
              },
              {
                icon: Users,
                title: "CRM Pipeline & Messaging",
                description: "Kanban board to manage candidates across stages, with notes, tags, bulk messaging, and reminders."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-obsidian-light p-5 sm:p-8 border-l-2 border-transparent hover:border-crimson transition-all duration-300 group card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-crimson/20 transition-colors">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
                </div>
                <h3 className="font-heading text-lg sm:text-xl font-bold mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-obsidian-light border-y border-[#222]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              HOW IT <span className="text-crimson">WORKS</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: "01", title: "Create Profile", desc: "Import from LinkedIn or upload your resume. Our AI structures your data." },
              { step: "02", title: "Get Matched", desc: "AI matches you with relevant opportunities or candidates based on your criteria." },
              { step: "03", title: "Connect & Grow", desc: "Interview with AI assistance, get insights, and land your dream role." }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="font-heading text-5xl sm:text-8xl font-bold text-crimson/10 absolute -top-4 sm:-top-8 left-0">{item.step}</div>
                <div className="pt-8 sm:pt-12 relative z-10">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold mb-2 sm:mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm sm:text-base">{item.desc}</p>
                </div>
                {index < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 w-6 h-6 sm:w-8 sm:h-8 text-crimson/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              CHOOSE YOUR <span className="text-crimson">TIER</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto mb-4">
              Transparent pricing. No hidden fees. Cancel anytime.
            </p>
            
            {/* Billing Period Toggle */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-6">
              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`px-2 sm:px-4 py-2 text-xs sm:text-sm transition-all ${
                  billingPeriod === "monthly" 
                    ? "text-white font-medium" 
                    : "text-gray-500 hover:text-gray-300"
                }`}
                data-testid="billing-monthly"
              >
                Monthly
              </button>
              <div 
                className="relative w-12 sm:w-14 h-6 sm:h-7 bg-obsidian-light border border-[#333] rounded-full cursor-pointer"
                onClick={() => setBillingPeriod(billingPeriod === "monthly" ? "annual" : "monthly")}
                data-testid="billing-toggle"
              >
                <div className={`absolute top-0.5 w-5 sm:w-6 h-5 sm:h-6 bg-crimson rounded-full transition-all ${
                  billingPeriod === "annual" ? "left-6 sm:left-7" : "left-0.5"
                }`} />
              </div>
              <button
                onClick={() => setBillingPeriod("annual")}
                className={`px-2 sm:px-4 py-2 text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 ${
                  billingPeriod === "annual" 
                    ? "text-white font-medium" 
                    : "text-gray-500 hover:text-gray-300"
                }`}
                data-testid="billing-annual"
              >
                Annual
                <span className="px-1.5 sm:px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] sm:text-xs rounded">
                  Save 17%
                </span>
              </button>
            </div>

            {/* Currency Selector */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="text-gray-500 text-sm">Currency:</span>
              <div className="relative">
                <button
                  onClick={() => setShowCurrencySelector(!showCurrencySelector)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-obsidian-light border border-[#333] text-white text-sm hover:border-crimson/50 transition-colors"
                  data-testid="currency-selector-btn"
                >
                  <span>{currencyInfo.symbol}</span>
                  <span>{currencyInfo.currency}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showCurrencySelector ? "rotate-90" : ""}`} />
                </button>
                
                {showCurrencySelector && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-obsidian border border-[#333] shadow-xl z-50 max-h-64 overflow-y-auto">
                    {availableCurrencies.map(curr => (
                      <button
                        key={curr.code}
                        onClick={() => handleCurrencyChange(curr.code)}
                        className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between hover:bg-crimson/10 ${
                          currencyInfo.currency === curr.code ? "bg-crimson/20 text-crimson" : "text-white"
                        }`}
                        data-testid={`currency-${curr.code}`}
                      >
                        <span>{curr.symbol} {curr.code}</span>
                        <span className="text-gray-500 text-xs">{curr.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {currencyInfo.country && (
                <span className="text-gray-500 text-xs">
                  (detected: {currencyInfo.country})
                </span>
              )}
            </div>

            <div className="inline-flex bg-obsidian-light border border-[#333] p-1">
              <button
                onClick={() => setPricingTab("recruiter")}
                className={`px-6 py-2 text-sm uppercase tracking-widest transition-all ${
                  pricingTab === "recruiter" ? "bg-crimson text-white" : "text-gray-400 hover:text-white"
                }`}
                data-testid="pricing-recruiter-tab"
              >
                Recruiters
              </button>
              <button
                onClick={() => setPricingTab("jobseeker")}
                className={`px-6 py-2 text-sm uppercase tracking-widest transition-all ${
                  pricingTab === "jobseeker" ? "bg-crimson text-white" : "text-gray-400 hover:text-white"
                }`}
                data-testid="pricing-jobseeker-tab"
              >
                Job Seekers
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(pricingTab === "recruiter" ? RECRUITER_TIERS : JOBSEEKER_TIERS).map((tier, index) => {
              const priceInfo = getTierPrice(tier.id, tier.price);
              return (
              <motion.div
                key={tier.id}
                className={`relative p-8 border transition-all ${
                  tier.highlighted 
                    ? "border-crimson bg-crimson/5 scale-105" 
                    : "border-[#222] bg-obsidian-light hover:border-crimson/50"
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-crimson text-xs uppercase tracking-widest">
                    Most Popular
                  </div>
                )}
                
                {billingPeriod === "annual" && priceInfo.savingsAmount > 0 && (
                  <div className="absolute -top-3 right-4 px-3 py-1 bg-green-500/20 border border-green-500/30 text-green-400 text-xs">
                    Save {priceInfo.savings}
                  </div>
                )}
                
                <h3 className="font-heading text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-5xl font-bold text-crimson">{priceInfo.formatted}</span>
                    <span className="text-gray-500">/mo</span>
                  </div>
                  {billingPeriod === "annual" && tier.price > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      {priceInfo.annualTotal} billed annually
                    </p>
                  )}
                </div>
                
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-crimson flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={handleGetStarted}
                  className={`w-full uppercase tracking-wider ${
                    tier.highlighted 
                      ? "bg-crimson hover:bg-crimson-hover text-white" 
                      : "bg-transparent border border-[#333] hover:border-crimson text-white"
                  }`}
                  data-testid={`pricing-${tier.id}-btn`}
                >
                  Get Started
                </Button>
              </motion.div>
            );
            })}
          </div>

          {/* Pay-as-you-go Section */}
          <motion.div
            className="mt-12 border border-[#222] bg-obsidian-light p-6 sm:p-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="payg-section"
          >
            <div className="flex items-center gap-3 mb-1">
              <ShoppingBag className="w-5 h-5 text-crimson" />
              <h3 className="font-heading text-xl sm:text-2xl font-bold">PAY AS YOU GO</h3>
            </div>
            <p className="text-sm text-gray-400 mb-6">No subscription needed. Buy only what you use.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {(pricingTab === "recruiter" ? RECRUITER_PAYG_ITEMS : JOBSEEKER_PAYG_ITEMS).map((item, i) => (
                <div key={i} className="p-5 bg-[#0a0a0a] border border-[#1a1a1a] hover:border-crimson/30 transition-colors" data-testid={`payg-item-${i}`}>
                  <h4 className="text-base font-bold text-white mb-1.5">{item.name}</h4>
                  <p className="text-sm text-gray-400 mb-3 leading-relaxed">{item.desc}</p>
                  <p className="text-sm text-crimson font-semibold">{item.prices}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button
                onClick={handleGetStarted}
                className="bg-transparent border border-crimson text-crimson hover:bg-crimson hover:text-white uppercase tracking-wider text-sm"
                data-testid="payg-get-started-btn"
              >
                Start Free & Buy Credits
              </Button>
            </div>
          </motion.div>

          {/* Feature Comparison Table */}
          <motion.div
            className={`mt-12 transition-all duration-700 ${comparisonHighlight ? "ring-2 ring-crimson/50 bg-crimson/[0.02]" : ""}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="comparison-table"
          >
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-center mb-6">
              COMPARE <span className="text-crimson">FEATURES</span>
            </h3>
            {(() => {
              const comparison = pricingTab === "recruiter" ? RECRUITER_COMPARISON : JOBSEEKER_COMPARISON;
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#333]">
                        <th className="text-left py-3 px-4 text-gray-400 text-xs uppercase tracking-wider font-normal w-1/3">Feature</th>
                        {comparison.tiers.map((tier, i) => (
                          <th key={tier} className={`text-center py-3 px-4 text-xs uppercase tracking-wider font-bold ${i === 1 ? "text-crimson" : "text-white"}`}>
                            {tier}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.features.map((row, i) => (
                        <tr key={i} className={`border-b border-[#1a1a1a] ${i % 2 === 0 ? "bg-[#0a0a0a]" : ""}`}>
                          <td className="py-3 px-4 text-gray-300 text-sm">{row.name}</td>
                          {row.values.map((val, j) => (
                            <td key={j} className="py-3 px-4 text-center">
                              {val === true ? (
                                <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                              ) : val === false ? (
                                <Minus className="w-4 h-4 text-gray-600 mx-auto" />
                              ) : (
                                <span className={`text-sm ${j === 2 ? "text-emerald-400 font-medium" : "text-gray-300"}`}>{val}</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-crimson/10 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-6xl font-bold mb-6">
            READY TO <span className="text-crimson">TRANSFORM</span><br />YOUR HIRING?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Join thousands of professionals who've already taken the red pill.
          </p>
          <Button 
            onClick={handleGetStarted}
            className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-12 py-6 text-lg glow-red"
            data-testid="cta-get-started-btn"
          >
            Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 px-6 bg-obsidian-light border-y border-[#222]">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">
              STAY <span className="text-crimson">AHEAD</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base mb-6">
              Get the latest job postings and career tips delivered to your inbox
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newsletterEmail.trim()) return;
                setSubscribing(true);
                try {
                  const res = await fetch(`${API}/newsletter/subscribe`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: newsletterEmail })
                  });
                  const data = await res.json();
                  if (res.ok) {
                    toast.success(data.message);
                    if (!data.already_subscribed) setNewsletterEmail("");
                  } else {
                    toast.error(data.detail || "Please enter a valid email");
                  }
                } catch { toast.error("Something went wrong"); }
                finally { setSubscribing(false); }
              }}
              className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 bg-obsidian border border-[#333] px-4 py-3 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                disabled={subscribing}
                className="bg-crimson hover:bg-crimson/80 text-white font-bold uppercase tracking-wider px-8"
                data-testid="newsletter-submit"
              >
                {subscribing ? "..." : "Subscribe"}
              </Button>
            </form>
            <p className="text-[10px] text-gray-600 mt-3">No spam. Unsubscribe anytime.</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#222]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-crimson flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">REKRUTOR</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-crimson transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-crimson transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-gray-500">© 2025 Rekrutor. All rights reserved.</p>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default LandingPage;
